import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.js";
import { AuthGate } from "./auth/AuthGate.js";
import { AdminApp } from "./admin/AdminApp.js";
import "./theme.css";

// Local dev bypass: set VITE_LOCAL_TOKEN + VITE_LOCAL_TENANT_ID in apps/web/.env
if (import.meta.env.VITE_LOCAL_TOKEN && import.meta.env.VITE_LOCAL_TENANT_ID) {
  window.__COREFINOPS__ = {
    localTestMode: true,
    token: import.meta.env.VITE_LOCAL_TOKEN,
    tenantId: import.meta.env.VITE_LOCAL_TENANT_ID,
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? "http://localhost:3000",
  };
}

/**
 * Bootstrap. Authentication (Cognito Hosted UI, Authorization Code + PKCE) and
 * tenant selection happen in AuthGate; the app renders only inside a signed-in
 * session. The browser holds ONLY the API access token — never DB credentials.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // Never retry authorization failures; the auth layer handles 401.
      retry: (count, err) => count < 1 && ![401, 403, 404].includes((err as unknown as { status?: number }).status ?? 0),
    },
  },
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("root element not found");

// The admin console (/admin/*) is a separate area that does NOT go through the
// tenant AuthGate: admins sign in and the isolated admin API decides access.
const isAdminRoute = window.location.pathname.startsWith("/admin");

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {isAdminRoute ? (
        <AdminApp />
      ) : (
        <AuthGate>
          <App />
        </AuthGate>
      )}
    </QueryClientProvider>
  </StrictMode>
);
